import { Head, Link, router } from "@inertiajs/react"
import { Card, DropdownMenu, Flex, IconButton, Text } from "@radix-ui/themes"
import { Archive, EllipsisVertical, RotateCcw } from "lucide-react"
import { z } from "zod"

import LinkButton from "@/components/link-button"
import CenteredLayout from "@/layouts/centered-layout"
import {
  archiveProjectPath,
  newProjectPath,
  projectDashboardPath,
  restoreProjectPath,
  sessionPath,
} from "@/routes"
import { ProjectSchema } from "@/schemas"

const PropsSchema = z.object({
  projects: z.array(ProjectSchema),
  archived_projects: z.array(ProjectSchema),
  current_user: z.object({
    id: z.number(),
    name: z.string(),
    email: z.string(),
  }),
  current_session: z.object({
    id: z.union([z.string(), z.number()]),
  }),
})

type Props = z.infer<typeof PropsSchema>

export default function Projects(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { projects, archived_projects, current_user, current_session } = validatedProps
  return (
    <>
      <CenteredLayout
        title="Projects"
        description="Manage your projects"
        footer={
          <div>
            <Flex direction="row" justify="between" align="center" p="4" gap="4">
              <Text>{current_user.email}</Text>

              <Text>•</Text>

              <button
                onClick={() => router.delete(sessionPath({ id: current_session.id }))}
                className="cursor-pointer border-none bg-transparent hover:underline"
              >
                Sign out
              </button>
            </Flex>
          </div>
        }
      >
        <Head title="Projects" />
        <Flex direction="column" gap="4">
          {projects && projects.length > 0 ? (
            <Flex direction="column" gap="3">
              {projects.map((project) => (
                <ProjectCard key={project.slug} project={project} />
              ))}
            </Flex>
          ) : (
            <Text>No active projects.</Text>
          )}

          <LinkButton href={newProjectPath()}>Create new project</LinkButton>

          {archived_projects.length > 0 && (
            <Flex direction="column" gap="3" mt="4">
              <Text size="2" weight="medium" color="gray">
                Archived projects
              </Text>
              {archived_projects.map((project) => (
                <ProjectCard key={project.slug} project={project} />
              ))}
            </Flex>
          )}
        </Flex>
      </CenteredLayout>
    </>
  )
}

function ProjectCard({ project }: { project: z.infer<typeof ProjectSchema> }) {
  const updateArchivedState = () => {
    const path = project.is_archived
      ? restoreProjectPath(project.slug)
      : archiveProjectPath(project.slug)

    router.patch(path)
  }

  return (
    <Card size="1">
      <Flex align="center" gap="2">
        <Link
          href={projectDashboardPath(project.slug)}
          style={{
            textDecoration: "none",
            color: "inherit",
            display: "block",
            flexGrow: 1,
          }}
        >
          <Flex direction="column" gap="2" p="2">
            <Text weight="medium">{project.name}</Text>
            {project.description && <Text>{project.description}</Text>}
          </Flex>
        </Link>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <IconButton
              variant="ghost"
              color="gray"
              mr="2"
              aria-label={`Actions for ${project.name}`}
            >
              <EllipsisVertical size={18} />
            </IconButton>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end">
            <DropdownMenu.Item onSelect={updateArchivedState}>
              {project.is_archived ? <RotateCcw size={16} /> : <Archive size={16} />}
              {project.is_archived ? "Restore project" : "Archive project"}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      </Flex>
    </Card>
  )
}
